import { ScrollArea } from '@/components/ui/scroll-area';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  ColumnDef,
  ColumnFiltersState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  Row,
  SortingState,
  useReactTable,
  VisibilityState,
} from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import { renderSimpleFinLinkCell } from './cell-simple-fin-account';
import {
  Account,
  AccountState,
  SimpleFinAccount,
  useAccounts,
  useCurrencyFormatter,
  useDateFormatter,
  useSimpleFinAccounts,
  useTimeFormatter,
} from '@liquidbudget/common';
import { useIsSmallScreen } from '@/components/ui/breakpoint';

export type BankSyncData = {
  account: Account;
  simpleFinAccount: SimpleFinAccount | undefined;
};

function CellDateTime({ isoDate }: { isoDate: string | null }) {
  const formatDate = useDateFormatter();
  const formatTime = useTimeFormatter();

  if (!isoDate) {
    return <div className="text-slate-400">Never</div>;
  }

  return (
    <div className="flex flex-col">
      <span className="font-medium">{formatTime(isoDate)}</span>
      <span className="text-sm text-muted-foreground">{formatDate(isoDate)}</span>
    </div>
  );
}

export function BankAccountTable() {
  const isSmallScreen = useIsSmallScreen();
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const currencyFormatter = useCurrencyFormatter();

  const accounts = useAccounts();
  const simpleFinAccounts = useSimpleFinAccounts();

  const tableData = useMemo(() => {
    return accounts
      .filter((account) => account.state === AccountState.ACTIVE)
      .map((account) => {
        const simpleFinAccount = simpleFinAccounts.find((sfa) => sfa.id === account.simpleFinAccountId);
        return {
          account,
          simpleFinAccount,
        };
      })
      .sort((a, b) => {
        const nameA = a.account.name.toLowerCase();
        const nameB = b.account.name.toLowerCase();
        return nameA.localeCompare(nameB);
      });
  }, [accounts, simpleFinAccounts]);

  const columns = useMemo<ColumnDef<BankSyncData>[]>(() => {
    const baseColumns = [
      {
        accessorKey: 'account.name',
        header: 'Account',
        cell: renderSimpleFinLinkCell,
      },
    ];

    if (!isSmallScreen) {
      baseColumns.push(
        {
          accessorKey: 'simpleFinAccount.balance',
          header: 'Balance',
          cell: ({ row }: { row: Row<BankSyncData> }) => {
            return (
              <div className="font-medium flex items-center gap-2">
                {!row.original.simpleFinAccount?.balance && <span className="text-slate-400">N/A</span>}
                {row.original.simpleFinAccount?.balance && (
                  <span className="">
                    {currencyFormatter(parseFloat(row.original.simpleFinAccount?.balance || '0'))}
                  </span>
                )}
              </div>
            );
          },
        },
        {
          accessorKey: 'simpleFinAccount.syncTime',
          header: 'Checked',
          cell: ({ row }: { row: Row<BankSyncData> }) => (
            <CellDateTime isoDate={row.original.simpleFinAccount?.syncTime ?? null} />
          ),
        },
        {
          accessorKey: 'account.simpleFinLastSyncTime',
          header: 'Latest',
          cell: ({ row }: { row: Row<BankSyncData> }) => (
            <CellDateTime isoDate={row.original.account.simpleFinLastSyncTime ?? null} />
          ),
        },
      );
    }

    return baseColumns;
  }, [isSmallScreen]);

  const table = useReactTable({
    data: tableData,
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
    },
  });

  return (
    <ScrollArea className="max-h-[65vh] mt-2 pr-2">
      <div className="w-full">
        <Table>
          <TableHeader className="bg-accent">
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  No accounts found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </ScrollArea>
  );
}
